import { useState } from 'react';
import { Search, X, Bell, CalendarOff } from 'lucide-react';

const KEY = 'spark-ticket-filter-v1';
const DEFAULT = { q: '', status: 'all', onlyNew: false, hideAppt: false };

export const ticketBucket = (t) => {
    const cat = t.status_category || t.statusCategory;
    const s = (t.status || '').toLowerCase();
    if (cat) return cat === 'done' ? 'done' : cat === 'indeterminate' ? 'progress' : 'open';
    if (/risol|chius|complet|evas|done|closed|fatto/.test(s)) return 'done';
    return /corso|lavorazione|progress|attesa di supporto|attesa di risposta|in carico|riapert/.test(s) ? 'progress' : 'open';
};
const isAppt = (t) => /appuntamento riservato/i.test(t.summary || t.title || '');

export const useTicketFilter = () => {
    const [f, setF] = useState(() => { try { return { ...DEFAULT, ...JSON.parse(localStorage.getItem(KEY) || '{}'), q: '' }; } catch (e) { return DEFAULT; } });
    const update = (patch) => setF(prev => {
        const n = { ...prev, ...patch };
        try { localStorage.setItem(KEY, JSON.stringify({ ...n, q: '' })); } catch (e) { /* storage pieno */ }
        return n;
    });
    return [f, update];
};

export const applyTicketFilter = (tickets, f, isNew) => {
    const q = f.q.trim().toLowerCase();
    return tickets.filter(t => {
        if (f.hideAppt && isAppt(t)) return false;
        if (f.onlyNew && !isNew(t)) return false;
        const b = ticketBucket(t);
        if (f.status === 'todo' && b !== 'open') return false;
        if (f.status === 'progress' && b !== 'progress') return false;
        if (f.status === 'done' && b !== 'done') return false;
        return !q || `${t.key || ''} ${t.summary || t.title || ''}`.toLowerCase().includes(q);
    });
};

const Chip = ({ active, onClick, children, testid, tone }) => (
    <button onClick={onClick} data-testid={testid}
        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1 whitespace-nowrap flex-shrink-0 ${active ? `${tone} text-white border-transparent shadow-sm` : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
        {children}
    </button>
);

export const TicketFilterBar = ({ tickets, f, update, isNew, shown }) => {
    const base = tickets.filter(t => !f.hideAppt || !isAppt(t));
    const n = (b) => base.filter(t => ticketBucket(t) === b).length;
    const news = tickets.filter(isNew).length;
    const filtered = f.q || f.status !== 'all' || f.onlyNew || f.hideAppt;
    return (
        <div className="space-y-2 mb-2 pb-2 border-b border-gray-200" data-testid="ticket-filter-bar">
            <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input value={f.q} onChange={e => update({ q: e.target.value })} placeholder="Cerca per codice, titolo o cliente…"
                    className="w-full pl-8 pr-8 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="ticket-filter-search" />
                {f.q && <button onClick={() => update({ q: '' })} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="ticket-filter-search-clear"><X className="w-4 h-4" /></button>}
            </div>
            <div className="flex flex-nowrap sm:flex-wrap gap-1.5 items-center overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1" data-testid="ticket-filter-chips">
                <Chip active={f.status === 'all'} onClick={() => update({ status: 'all' })} tone="bg-blue-600" testid="ticket-filter-all">Tutti ({base.length})</Chip>
                <Chip active={f.status === 'todo'} onClick={() => update({ status: 'todo' })} tone="bg-gray-500" testid="ticket-filter-todo"><span className="w-2 h-2 rounded-full bg-gray-400" />Da gestire ({n('open')})</Chip>
                <Chip active={f.status === 'progress'} onClick={() => update({ status: 'progress' })} tone="bg-yellow-500" testid="ticket-filter-progress"><span className="w-2 h-2 rounded-full bg-yellow-400" />In corso ({n('progress')})</Chip>
                <Chip active={f.status === 'done'} onClick={() => update({ status: 'done' })} tone="bg-green-600" testid="ticket-filter-done"><span className="w-2 h-2 rounded-full bg-green-500" />Risolti ({n('done')})</Chip>
                <span className="w-px h-5 bg-gray-200 mx-0.5 flex-shrink-0" />
                <Chip active={f.onlyNew} onClick={() => update({ onlyNew: !f.onlyNew })} tone="bg-red-500" testid="ticket-filter-new"><Bell className="w-3 h-3" />Solo novità ({news})</Chip>
                <Chip active={f.hideAppt} onClick={() => update({ hideAppt: !f.hideAppt })} tone="bg-indigo-600" testid="ticket-filter-hide-appt"><CalendarOff className="w-3 h-3" />Nascondi appuntamenti</Chip>
            </div>
            {filtered && (
                <p className="text-[11px] text-gray-500 flex items-center gap-2" data-testid="ticket-filter-count">
                    {shown} di {tickets.length} ticket
                    <button onClick={() => update({ ...DEFAULT })} className="text-blue-600 hover:underline" data-testid="ticket-filter-reset">Azzera filtri</button>
                </p>
            )}
        </div>
    );
};
